import BackButton from "../../components/common/backButton";
import { useNavigate } from "react-router-dom";
import ico_chat_gray from "../../images/authentication/ico_chat_gray.png";
import Bell from "../../images/authentication/Bell.png";
import ChevronRight from "../../images/authentication/Chevron_Right.png";
import BottomSheet from "../../components/common/BottomSheet";
import { useState, useEffect, useCallback } from "react";
import userHooks from "../../hooks/userHooks";
import useDebounce from "../../hooks/useDebounce";
import { z } from "zod";
import Toast from "../../components/common/toast";
import useAuthStore from "../../store/authStore";

const phoneSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, "휴대폰 번호를 입력해주세요.")
    .refine((val) => /^010\d{8}$/.test(val.replace(/-/g, "")), {
      message: "올바른 휴대폰 번호를 입력해주세요.",
    }),
});

const formatPhoneNumber = (value) => {
  const numbers = value.replace(/[^\d]/g, "");

  if (numbers.length <= 3) {
    return numbers;
  } else if (numbers.length <= 7) {
    return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
  } else {
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(
      7,
      11
    )}`;
  }
};

const Authentication = () => {
  const navigate = useNavigate();
  const userInfo = useAuthStore((state) => state.userInfo);
  const isSignedUp = userInfo?.isSignedUp;
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isConsultBottomSheetOpen, setIsConsultBottomSheetOpen] =
    useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [timeLeft, setTimeLeft] = useState(180);

  const [toastMessage, setToastMessage] = useState("");
  const [isToastOpen, setIsToastOpen] = useState(false);

  const debouncedPhoneNumber = useDebounce(phoneNumber, 500);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setIsVerified(false);
    setIsVerificationSent(false);
    setVerificationCode("");
    setTimeLeft(180);
    setPhoneError("");
    setVerificationError("");
    setPhoneNumber("");
  }, []);

  useEffect(() => {
    let timer;
    if (isVerificationSent && timeLeft > 0 && !isVerified) {
      timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            setIsVerificationSent(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isVerificationSent, timeLeft, isVerified]);

  const handleVerifyCode = useCallback(async () => {
    try {
      if (!verificationCode || verificationCode.length !== 6) {
        setVerificationError("6자리 인증번호를 입력해주세요.");
        return;
      }

      setIsLoading(true);
      const response = await userHooks.verifySmsHooks({
        hp: phoneNumber.replace(/-/g, ""),
        code: verificationCode,
      });

      if (response.IsError) {
        setVerificationError("인증번호가 일치하지 않습니다.");
        return;
      }

      setIsVerified(true);
      setVerificationError("");
    } catch (error) {
      console.error(error);
      setVerificationError("인증번호 확인 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  }, [verificationCode, phoneNumber]);

  // 인증번호가 6자리가 되면 자동으로 검증
  useEffect(() => {
    if (
      verificationCode.length === 6 &&
      isVerificationSent &&
      !isVerified &&
      !isLoading
    ) {
      handleVerifyCode();
    }
  }, [
    verificationCode,
    isVerificationSent,
    isVerified,
    isLoading,
    handleVerifyCode,
  ]);

  useEffect(() => {
    if (debouncedPhoneNumber) {
      const result = phoneSchema.safeParse({
        phoneNumber: debouncedPhoneNumber,
      });
      if (!result.success) {
        setPhoneError(result.error.errors[0].message);
      } else {
        setPhoneError("");
      }
    }
  }, [debouncedPhoneNumber]);

  // const formatTime = (seconds) => {
  //   const minutes = Math.floor(seconds / 60);
  //   const remainingSeconds = seconds % 60;
  //   return `${String(minutes).padStart(2, "0")}:${String(
  //     remainingSeconds
  //   ).padStart(2, "0")}`;
  // };

  const handleSendVerification = async () => {
    try {
      const result = phoneSchema.safeParse({ phoneNumber });

      if (!result.success) {
        setPhoneError(result.error.errors[0].message);
        return;
      }

      setPhoneError("");
      setIsLoading(true);
      const response = await userHooks.sendSmsHooks({
        hp: phoneNumber.replace(/-/g, ""),
      });

      if (response.IsError) {
        setPhoneError("인증번호 전송에 실패했습니다. 다시 시도해주세요.");
        return;
      }

      setIsVerificationSent(true);
      setTimeLeft(180);
      setPhoneError("");
    } catch (error) {
      console.error(error);
      setPhoneError("인증번호 전송 중 오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(formatPhoneNumber(value));
  };

  const handleVerificationCodeChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
    setVerificationCode(value);
    setVerificationError("");
  };

  const handleComplete = async () => {
    try {
      if (!isVerified) {
        setVerificationError("인증번호를 확인해주세요.");
        return;
      }

      setIsLoading(true);
      const response = await userHooks.updateMessageAllowHooks({
        messageAllow: true,
      });

      if (response.IsError) {
        setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
        setIsToastOpen(true);
        setIsLoading(false);
        return;
      }

      const currentStorage = localStorage.getItem(
        "dplanit-moming-auth-storage"
      );
      if (currentStorage) {
        const storageData = JSON.parse(currentStorage);
        const newState = {
          ...storageData.state,
          userInfo: {
            ...storageData.state.userInfo,
            message_allow: true,
          },
        };

        const newStorageData = {
          state: newState,
          version: 0,
        };
        localStorage.setItem(
          "dplanit-moming-auth-storage",
          JSON.stringify(newStorageData)
        );
      }

      setToastMessage("신청이 완료되었습니다. 매 주수 별 1일차에 알림톡이 전송됩니다.");
      setIsToastOpen(true);
      setTimeout(() => {
        window.location.replace("/");
      }, 4000);
    } catch (error) {
      console.error("알림톡 신청 에러:", error);
      setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
      setIsToastOpen(true);
      setIsLoading(false);
    }
  };

  const handleNext = async () => {
    try {
      if (!isVerified) {
        setVerificationError("인증번호를 확인해주세요.");
        return;
      }

      setIsLoading(true);
      const response = await userHooks.updateMessageAllowHooks({
        messageAllow: true,
      });

      if (response.IsError) {
        setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
        setIsToastOpen(true);
        setIsLoading(false);
        return;
      }

      const currentStorage = localStorage.getItem(
        "dplanit-moming-auth-storage"
      );
      if (currentStorage) {
        const storageData = JSON.parse(currentStorage);
        const newState = {
          ...storageData.state,
          userInfo: {
            ...storageData.state.userInfo,
            message_allow: true,
          },
        };

        const newStorageData = {
          state: newState,
          version: 0,
        };
        localStorage.setItem(
          "dplanit-moming-auth-storage",
          JSON.stringify(newStorageData)
        );
      }

      setToastMessage("신청이 완료 되었습니다. 임신 주차별 1일차 마다 메시지가 전송됩니다.");
      setIsToastOpen(true);
      setTimeout(() => {
        navigate("/mypage/baby-info", { replace: true });
      }, 3000);
    } catch (error) {
      console.error("알림톡 신청 에러:", error);
      setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
      setIsToastOpen(true);
      setIsLoading(false);
    }
  };

  const handleSkip = async () => {
    try {
      if (isSignedUp) {
        navigate("/", { replace: true });
        return;
      }

      const response = await userHooks.updateMessageAllowHooks({
        messageAllow: false,
      });

      if (response.IsError) {
        setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
        setIsToastOpen(true);
        return;
      }

      navigate("/mypage/baby-info", { replace: true });
    } catch (error) {
      console.error("알림톡 신청 에러:", error);
      setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
      setIsToastOpen(true);
    }
  };

  return (
    <div
      className="flex flex-col bg-white pt-[3.125rem] md:pt-[4.375rem]"
      style={{ 
        minHeight: "calc(var(--vh, 1vh) * 100)",
        paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
      }}
    >
      <BackButton text="휴대폰 번호 입력" />
      <div className="flex flex-col w-[calc(100%-46px)] mx-auto px-0 md:px-[80px]">
        <p className="text-[16px] text-[#040404] mb-2 pt-10">
          석박사 및 전문의료인이 직접 구성한
          <br />
          <strong>주수별 맞춤 알림톡</strong>과<br />
          <strong>1:1 전문가 상담</strong>을 위한
          <br />
          핸드폰 번호를 입력해주세요.
        </p>
        <p className="text-[12px] text-[#888888]">
          시범서비스 기간엔 전부 무료이며 언제든 취소 가능합니다
        </p>
      </div>

      <div className="flex flex-col w-[calc(100%-46px)] mx-auto pt-10 px-0 md:px-[80px]">
        <div className="flex flex-col space-y-[30px]">
          <div className="relative">
            <input
              type="tel"
              placeholder="휴대폰 번호"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              className={`w-full h-[38px] border-b ${
                phoneError ? "border-red-500" : "border-[#E9EAEE]"
              } text-[18px] font-medium pr-[80px] focus:outline-none placeholder:text-[#BDBEC6]`}
            />
            {phoneError && (
              <p className="absolute -bottom-6 left-0 text-red-500 text-sm">
                {phoneError}
              </p>
            )}
            <button
              className="absolute right-0 bottom-[5px] h-[32px] px-[19px] flex items-center justify-center text-[12px] bg-black text-white rounded-[4px] disabled:bg-gray-400"
              onClick={handleSendVerification}
              disabled={isLoading || isVerified}
            >
              {isLoading ? "전송중..." : "인증"}
            </button>
          </div>
          <div className="relative">
            <input
              type="text"
              placeholder="인증번호 (숫자 6자리)"
              value={verificationCode}
              onChange={handleVerificationCodeChange}
              className={`w-full h-[38px] border-b ${
                verificationError ? "border-red-500" : "border-[#E9EAEE]"
              } text-[18px] font-medium focus:outline-none placeholder:text-[#BDBEC6] 
              [&:-webkit-autofill]:bg-white [&:-webkit-autofill:hover]:bg-white [&:-webkit-autofill:focus]:bg-white [&:-webkit-autofill:active]:bg-white
              [&:-webkit-autofill]:shadow-[inset_0_0_0_1000px_white] [&:-webkit-autofill:hover]:shadow-[inset_0_0_0_1000px_white] [&:-webkit-autofill:focus]:shadow-[inset_0_0_0_1000px_white`}
              autoComplete="off"
            />
            {verificationError && (
              <p className="absolute -bottom-6 left-0 text-red-500 text-sm">
                {verificationError}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col mx-auto pt-[108px] space-y-[6px]">
          <button
            className="flex justify-center items-center gap-[9px] w-[268px] h-[40px] bg-[#F5F5F5] rounded-[10px] text-[14px] leading-[20px] tracking-[0%] align-middle"
            onClick={() => setIsBottomSheetOpen(true)}
          >
            <img src={Bell} alt="" className="w-[18px] h-[20px]" />
            주수별 맞춤 알림톡은 무료인가요?
          </button>
          <button
            className="flex justify-center items-center gap-[9px] w-[268px] h-[40px] bg-[#F5F5F5] rounded-[10px] text-[14px] leading-[20px] tracking-[0%] align-middle"
            onClick={() => setIsConsultBottomSheetOpen(true)}
          >
            <img src={ico_chat_gray} alt="" className="w-[24px] h-[24px]" />
            1:1 전문가 상담은 어떻게 받나요?
          </button>
        </div>

        <button
          className="inline-flex items-center justify-center gap-[0.25rem] text-[#FF931D] font-bold my-[30px] mx-auto"
          onClick={handleSkip}
        >
          <span className="text-[0.875rem] font-bold leading-none tracking-normal text-center align-middle text-[#FF931D]">건너뛰기</span>
          <img 
            src={ChevronRight} 
            alt="chevron right" 
            className="w-[0.875rem] h-[0.875rem] md:w-[1.125rem] md:h-[1.125rem]"
          />
        </button>

        <div className="w-full flex justify-center">
          <button
            className={`w-full h-[54px] text-[15px] font-semibold rounded-[8px] transition-all duration-200 ${
              isVerified && !isLoading
                ? "bg-[#FF931D] text-white hover:bg-[#E6820A]"
                : "bg-[#E9EAEE] text-[#CCCCCC] cursor-not-allowed"
            }`}
            onClick={isSignedUp ? handleComplete : handleNext}
            disabled={!isVerified || isLoading}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>완료</span>
              </div>
            ) : (
              <span>{isSignedUp ? "완료" : "다음"}</span>
            )}
          </button>
        </div>
      </div>

      <BottomSheet
        isOpen={isBottomSheetOpen}
        onClose={() => setIsBottomSheetOpen(false)}
        className="rounded-t-[30px]"
      >
        <div className="flex flex-col px-[4px] pb-[100px] md:px-[20px]">
          <h2 className="pt-[40px] text-[24px] font-semibold leading-[32px] tracking-[0%] align-middle mb-[28px] whitespace-pre-line md:whitespace-normal">
            {`주수별 맞춤 알림톡은 
            무엇인가요?`}
          </h2>
          <div className="flex flex-col gap-y-[10px]">
            <p className="text-[16px] md:text-[18px] font-normal leading-[22px] whitespace-pre-line md:whitespace-normal">
              {`네, 지금은 6개월간 시범 서비스로 전부 무료로 만나보실 수 있어요!`}
            </p>
            <p className="text-[16px] md:text-[18px] font-normal leading-[22px] whitespace-pre-line md:whitespace-normal">
              {`임신 주수별 엄마의 변화, 태아 성장, 시기별 검진 정보는 기본, 선배맘들이 강력 추천한 '찐 꿀팁'까지 담았어요.`}
            </p>
            <p className="text-[16px] md:text-[18px] font-normal leading-[22px] whitespace-pre-line md:whitespace-normal">
              {`매주 화·금 오전 10시, 카카오 알림톡이 똑똑!`}
            </p>
            <p className="text-[16px] md:text-[18px] font-normal leading-[22px] whitespace-pre-line md:whitespace-normal">
              {`지금 바로, 디플래닛 맘잉의 알림톡을 체험해 보세요.`}
            </p>
          </div>
        </div>
      </BottomSheet>

      <BottomSheet
        isOpen={isConsultBottomSheetOpen}
        onClose={() => setIsConsultBottomSheetOpen(false)}
        className="rounded-t-[30px]"
      >
        <div className="flex flex-col px-[4px] pb-[100px] md:px-[20px]">
          <h2 className="pt-[40px] text-[24px] font-semibold leading-[32px] tracking-[0%] align-middle mb-[28px] whitespace-pre-line md:whitespace-normal">
            {`1:1 전문가 상담은
            어떻게 받나요?`}
          </h2>
          <div className="flex flex-col gap-y-[10px]">
            <p className="text-[16px] md:text-[18px] font-normal leading-[22px] tracking-[0%] whitespace-pre-line md:whitespace-normal">
              {`맘카페나 검색만으로는 해결되지 않는 임신·출산·육아 고민, 답답하셨죠?`}
            </p>
            <p className="text-[16px] md:text-[18px] font-normal leading-[22px] tracking-[0%] whitespace-pre-line md:whitespace-normal">
              {`로그인만 하면, 화면 하단에 전문가 상담 버튼이 짠!`}
            </p>

            <p className="text-[16px] md:text-[18px] font-normal leading-[22px] tracking-[0%] whitespace-pre-line md:whitespace-normal">
              {`버튼을 누르면 디플래닛 맘잉의 전문 상담사와 바로 연결돼요.`}
            </p>
            <p className="text-[16px] md:text-[18px] font-normal leading-[22px] tracking-[0%] whitespace-pre-line md:whitespace-normal">
              {`속 시원한 답을 원할 때 먼저 생각나는 든든한
              내편! 당신의 걱정을 덜어드립니다.`}
            </p>
          </div>
        </div>
      </BottomSheet>

      <Toast
        message={toastMessage}
        isOpen={isToastOpen}
        onClose={() => setIsToastOpen(false)}
      />
    </div>
  );
};

export default Authentication;
